import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Contact } from 'model/contacts.model';
import { ContactService } from 'service/contact.service';

type FetchContactsArgs = {
  mine: boolean;
  params?: Record<string, any>;
};

type ContactsState = {
  contacts: Contact[];
  total: number;
  isLoading: boolean;
  error: string | null;
  companyOptions: string[];
  isDropdownLoading: boolean;
  notes?: any[];
};
const initialState: ContactsState = {
  contacts: [],
  total: 0,
  isLoading: false,
  error: null,
  companyOptions: [],
  isDropdownLoading: false,
  notes: [],
};

export const fetchAllContacts = createAsyncThunk(
  'contacts/fetchAllContacts',
  async ({ mine, params }: FetchContactsArgs, { rejectWithValue }) => {
    try {
      const res = await ContactService.getAllContacts(mine, params);
      return res;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch contacts');
    }
  },
);

export const fetchContactDropdowns = createAsyncThunk(
  'contacts/fetchContactDropdowns',
  async (_, { rejectWithValue }) => {
    try {
      const res = await ContactService.getContactDropdowns();
      return res;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch contact dropdowns');
    }
  },
);

export const fetchContactNotes = createAsyncThunk(
  'contacts/fetchContactNotes',
  async (contactId: string, { rejectWithValue }) => {
    try {
      const res = await ContactService.getNotes(contactId);
      return res;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch contact notes');
    }
  },
);

const contactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contacts = action.payload.data || [];
        state.total = action.payload.total;
      })
      .addCase(fetchAllContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch contacts';
      })
      .addCase(fetchContactDropdowns.pending, (state) => {
        state.isDropdownLoading = true;
      })
      .addCase(fetchContactDropdowns.fulfilled, (state, action) => {
        state.isDropdownLoading = false;
        state.companyOptions = action.payload;
      })
      .addCase(fetchContactDropdowns.rejected, (state) => {
        state.isDropdownLoading = false;
      })
      .addCase(fetchContactNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContactNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notes = action.payload.notes || [];
      })
      .addCase(fetchContactNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch contact notes';
      });
  },
});

export default contactSlice.reducer;
